import {
  LabeledBerekenDate,
  LabeledBevestigingInput,
  LabeledCurrencyInput,
  LabeledDateInput,
  LabeledNumberInput,
  LabeledPercentageInput,
  LabeledRadioInput,
  LabeledSelectInput,
  LabeledTextInput
} from "adviesbox-shared";
import { FormikProps } from "formik";
import React, { ReactElement } from "react";
import Modal from "../../shared/components/modal/Modal";
import LabeledHdnKeuzelijst from "../../shared/components/hdn-keuzelijst/LabeledHdnKeuzelijst";
import { ISWSideEffects } from "../../shared/components/isw-side-effects/isw-side-effects";
import AdviesBoxFormik from "../../shared/utils/adviesbox-formik";
import { enum2options } from "../../shared/utils/enum-to-options";
import { getInkomenEnFiscusTextResources } from "../infra/inkomen-en-fiscus-resources";
import {
  ondernemingModalSchema,
  OndernemingModalType,
  RechtsVorm,
  SoortInkomenverklaring,
  SoortOnderneming
} from "../infra/inkomen-en-fiscus-schema";
import Inkomensverleden, { WinstCaption, WinstSoort } from "../infra/shared-components/inkomensverleden";
import ScenarioInstellingen from "../infra/shared-components/scenario-instellingen";
import { ondernemingModalSideEffects } from "./onderneming-modal-side-effects";

export type OndernemingModalData = {
  data: OndernemingModalType;
};
type OndernemingModalProps = {
  onSave?: (data: OndernemingModalType) => void;
  closeModal?: () => void;
};

const OndernemingModal = ({ data, onSave, closeModal }: OndernemingModalData & OndernemingModalProps): ReactElement => {
  return (
    <AdviesBoxFormik<OndernemingModalType>
      initialValues={{ ...data }}
      validationSchema={ondernemingModalSchema}
      onSave={onSave}
      closeModal={closeModal}
      render={({
        values: { dga, verklaringInkomen },
        submitForm,
        dirty
      }: FormikProps<OndernemingModalType>): ReactElement => {
        const winstSoort = dga ? WinstSoort.Arbeid : WinstSoort.Onderneming;
        const showAccountantFields = verklaringInkomen === SoortInkomenverklaring.Accountantsverklaring;
        const body = (
          <>
            <h3>
              <b>Gegevens (hoofd)onderneming</b>
            </h3>
            <LabeledSelectInput
              caption="Soort onderneming"
              name="soortOnderneming"
              options={enum2options(SoortOnderneming)}
              labelColSize={6}
            />
            <LabeledSelectInput
              caption="Rechtsvorm"
              name="rechtsvorm"
              options={enum2options(RechtsVorm)}
              labelColSize={6}
            />
            <LabeledDateInput
              caption="Ingangsdatum onderneming"
              name="ingangsdatumOnderneming"
              tooltip={getInkomenEnFiscusTextResources("TooltipOndernemingIngangsdatum")}
              labelColSize={6}
              fieldSize="l"
            />
            <LabeledBerekenDate
              caption="Inkomen meetellen tot"
              name="inkomenMeetellenTot"
              fieldSize="l"
              labelColSize={6}
            />
            <LabeledTextInput caption="KvK-nummer" name="kvkNummer" labelColSize={6} />
            <LabeledTextInput caption="SBI-code" name="sbiCode" labelColSize={6} />
            <LabeledTextInput caption="RSIN-nummer" name="rsinNummer" labelColSize={6} />
            <LabeledBevestigingInput
              caption="DGA"
              name="dga"
              tooltip={getInkomenEnFiscusTextResources("TooltipOndernemingDga")}
              labelColSize={6}
            />
            {dga && (
              <LabeledPercentageInput
                caption="Aandelen percentage"
                name="aandelenPercentage"
                decimalen={2}
                labelColSize={6}
              />
            )}
            <h3 className="mt-4">
              <b>Verklaring inkomen</b>
            </h3>
            <LabeledRadioInput
              caption="Verklaring inkomen"
              name="verklaringInkomen"
              labelColSize={6}
              options={[
                {
                  label: "IB stukken",
                  value: SoortInkomenverklaring.IbStukken
                },
                {
                  label: "Accountantsverklaring",
                  value: SoortInkomenverklaring.Accountantsverklaring
                }
              ]}
            />
            {showAccountantFields && (
              <>
                <LabeledCurrencyInput caption="Toetsinkomen" name="toetsinkomen" labelColSize={6} />
                <LabeledNumberInput
                  caption="Aantal ondernemingen"
                  name="aantalOndernemingen"
                  labelColSize={6}
                  fieldSize="l"
                />
                <LabeledTextInput
                  caption="BOI"
                  name="boi"
                  tooltip={getInkomenEnFiscusTextResources("TooltipOndernemingBoi")}
                  labelColSize={6}
                />
                <LabeledTextInput caption="Dossiernummer" name="dossiernummer" labelColSize={6} />
                <LabeledDateInput caption="Datum uitgifte" name="uitgifteDatum" labelColSize={6} fieldSize="l" />
                <LabeledHdnKeuzelijst
                  caption="Rekenexpert"
                  name="rekenexpert"
                  berichtSoortType="AX"
                  keuzelijst="RekenexpertType"
                  labelColSize={6}
                />
              </>
            )}

            <h3 className="mt-3">
              <b>{WinstCaption(winstSoort)}</b>
            </h3>
            <Inkomensverleden soort={winstSoort} name="inkomensverleden" />
            <ScenarioInstellingen name="scenarioInstellingen" eindDatumOverrule={true} />
          </>
        );
        return (
          <>
            <ISWSideEffects<OndernemingModalType> sync={ondernemingModalSideEffects()} />

            <Modal
              title="Onderneming"
              body={body}
              onSubmitClick={submitForm}
              onCancelClick={closeModal}
              isFormDirty={dirty}
            />
          </>
        );
      }}
    />
  );
};

export default OndernemingModal;
